import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isAdminRole, type Role } from '../api.ts';
import { t } from '../i18n/index.ts';
import {
  EvidenceReasonRequired,
  listAssets,
  type EvidenceAsset,
  type ReasonChallenge,
} from '../lib/evidence.ts';
import {
  createCase,
  getCase,
  linkAsset,
  listCases,
  unlinkAsset,
  type CaseDetail,
  type CaseListRow,
} from '../lib/evidence-workspace.ts';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  Label,
  Metric,
  Metrics,
  Mono,
  Row,
  Section,
  Select,
  Spinner,
  Status,
  Table,
  sentence,
  type Tone,
} from '../ui.tsx';
import { EvidenceGate, formatWhen, shortId } from './evidence-gate.tsx';

/**
 * Cases — group evidence assets for a matter or investigation.
 *
 * Linking and unlinking are not mutations of an asset; the service records them
 * as custody events on the append-only chain (`evac-evidence-service`
 * `src/evidence/cases.ts`). The screen says so plainly and never implies an
 * asset was changed.
 */

const CASE_TONE: Record<string, Tone> = {
  open: 'safe',
  closed: 'neutral',
  archived: 'neutral',
};

const ASSET_TONE: Record<string, Tone> = {
  stored: 'safe',
  quarantined: 'attention',
  voided: 'neutral',
  sealed_illegal: 'alert',
  disposed: 'neutral',
};

export function Cases({
  orgId,
  role,
  onGoToEvidence,
}: {
  orgId: string;
  role: Role;
  onGoToEvidence?: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<ReasonChallenge | null>(null);
  const canWrite = isAdminRole(role);

  const cases = useQuery({
    queryKey: ['evidence-cases', orgId],
    queryFn: async () => {
      try {
        return await listCases(orgId);
      } catch (e) {
        if (e instanceof EvidenceReasonRequired) {
          setChallenge(e.challenge);
          return { items: [], next_cursor: null };
        }
        throw e;
      }
    },
    retry: false,
  });

  const onStated = () => {
    setChallenge(null);
    void qc.invalidateQueries({ queryKey: ['evidence-cases', orgId] });
  };

  return (
    <EvidenceGate
      title={t('cases.title')}
      sub={t('cases.sub')}
      err={cases.error}
      loading={cases.isLoading}
      challenge={challenge}
      onStated={onStated}
      onGoToEvidence={onGoToEvidence}
    >
      {selected !== null ? (
        <CaseView
          orgId={orgId}
          caseId={selected}
          canWrite={canWrite}
          onBack={() => setSelected(null)}
        />
      ) : (
        <CaseList
          orgId={orgId}
          canWrite={canWrite}
          list={cases.data?.items ?? []}
          onOpen={setSelected}
        />
      )}
    </EvidenceGate>
  );
}

function CaseList({
  orgId,
  canWrite,
  list,
  onOpen,
}: {
  orgId: string;
  canWrite: boolean;
  list: CaseListRow[];
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [ref, setRef] = useState('');
  const [error, setError] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createCase(orgId, { title: title.trim(), externalRef: ref.trim() || undefined }),
    onSuccess: (created) => {
      setTitle('');
      setRef('');
      setError('');
      void qc.invalidateQueries({ queryKey: ['evidence-cases', orgId] });
      onOpen(created.id);
    },
    onError: (e: Error) => setError(e.message),
  });

  const open = list.filter((c) => c.state === 'open').length;

  return (
    <>
      <div className="mb-6">
        <Metrics>
          <Metric label={t('cases.metric.cases')} value={list.length} />
          <Metric
            label={t('cases.metric.open')}
            value={open}
            tone={open > 0 ? 'safe' : 'neutral'}
          />
        </Metrics>
      </div>

      {canWrite ? (
        <Section title={t('cases.new.title')}>
          {error ? <Finding tone="alert">{error}</Finding> : null}
          <form
            className="flex max-w-[560px] flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (title.trim()) create.mutate();
            }}
          >
            <Field
              label={t('cases.new.field.title')}
              value={title}
              onChange={setTitle}
              placeholder="Operation name, matter…"
            />
            <Field
              label={t('cases.new.field.ref')}
              value={ref}
              onChange={setRef}
              placeholder="Court file, brief number…"
              hint="Your own reference for this matter — carried on the case, not verified."
            />
            <div>
              <Button type="submit" variant="primary" disabled={!title.trim() || create.isPending}>
                {create.isPending ? t('cases.new.creating') : t('cases.new.create')}
              </Button>
            </div>
          </form>
        </Section>
      ) : null}

      <Section title={t('cases.list.title')}>
        {list.length === 0 ? (
          <Empty>{t('cases.list.empty')}</Empty>
        ) : (
          <Table
            head={[
              t('cases.head.title'),
              t('cases.head.reference'),
              t('cases.head.state'),
              t('cases.head.assets'),
              t('cases.head.created'),
              '',
            ]}
          >
            {list.map((c) => (
              <Row key={c.id}>
                <Cell wide>{c.title}</Cell>
                <Cell>{c.external_ref ? <Mono>{c.external_ref}</Mono> : '—'}</Cell>
                <Cell>
                  <Status tone={CASE_TONE[c.state] ?? 'neutral'}>{sentence(c.state)}</Status>
                </Cell>
                <Cell numeric>{c.linked_count}</Cell>
                <Cell>{formatWhen(c.created_at)}</Cell>
                <Cell>
                  <Button
                    variant="quiet"
                    label={`Open case ${c.title}`}
                    onClick={() => onOpen(c.id)}
                  >
                    {t('cases.open')}
                  </Button>
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}

function CaseView({
  orgId,
  caseId,
  canWrite,
  onBack,
}: {
  orgId: string;
  caseId: string;
  canWrite: boolean;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [toLink, setToLink] = useState('');

  const detail = useQuery({
    queryKey: ['evidence-case', orgId, caseId],
    queryFn: () => getCase(orgId, caseId),
    retry: false,
  });

  // The stored assets available to link — filtered to those not already on the
  // case. Loaded only when a custodian can act on them.
  const assets = useQuery({
    queryKey: ['evidence-assets', orgId],
    queryFn: () => listAssets(orgId),
    enabled: canWrite,
    retry: false,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['evidence-case', orgId, caseId] });
    void qc.invalidateQueries({ queryKey: ['evidence-cases', orgId] });
  };

  const link = useMutation({
    mutationFn: (assetId: string) => linkAsset(orgId, caseId, assetId),
    onSuccess: () => {
      setToLink('');
      setError('');
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const unlink = useMutation({
    mutationFn: (assetId: string) => unlinkAsset(orgId, caseId, assetId),
    onSuccess: () => {
      setError('');
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const back = (
    <div className="mb-4">
      <Button variant="quiet" onClick={onBack}>
        {t('cases.back')}
      </Button>
    </div>
  );

  if (detail.isLoading) {
    return (
      <>
        {back}
        <Spinner />
      </>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <>
        {back}
        <Finding tone="alert">
          {(detail.error as Error | null)?.message ?? t('common.notFound')}
        </Finding>
      </>
    );
  }

  const d: CaseDetail = detail.data;
  const linkedIds = new Set(d.assets.map((a) => a.asset_id));
  const linkable = (assets.data?.items ?? []).filter(
    (a: EvidenceAsset) => a.state === 'stored' && !linkedIds.has(a.id),
  );

  return (
    <>
      {back}
      <Section
        title={d.case.title}
        action={
          <Status tone={CASE_TONE[d.case.state] ?? 'neutral'}>{sentence(d.case.state)}</Status>
        }
      >
        <div className="grid max-w-[720px] grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-[14px]">
          <Label>Identifier</Label>
          <Mono>{d.case.id}</Mono>
          <Label>Reference</Label>
          <span>{d.case.external_ref ? <Mono>{d.case.external_ref}</Mono> : 'None'}</span>
          <Label>Created</Label>
          <span>{formatWhen(d.case.created_at)}</span>
        </div>
      </Section>

      {error ? <Finding tone="alert">{error}</Finding> : null}

      {canWrite ? (
        <Section title={t('cases.link.title')}>
          {linkable.length === 0 ? (
            <p className="text-[14px] text-[var(--ink-2)]">
              {assets.isLoading ? t('common.loadingCommitted') : t('cases.link.none')}
            </p>
          ) : (
            <div className="flex max-w-[560px] items-end gap-3">
              <div className="flex-1">
                <Select
                  label={t('common.committedEvidence')}
                  value={toLink}
                  onChange={setToLink}
                  options={[
                    { value: '', label: t('common.chooseAsset') },
                    ...linkable.map((a: EvidenceAsset) => ({
                      value: a.id,
                      label: `${a.media_type} · ${a.merkle_root.slice(0, 12)}… · ${formatWhen(a.captured_at)}`,
                    })),
                  ]}
                />
              </div>
              <Button
                variant="primary"
                disabled={!toLink || link.isPending}
                onClick={() => toLink && link.mutate(toLink)}
              >
                {link.isPending ? t('cases.link.linking') : t('cases.link.cta')}
              </Button>
            </div>
          )}
          <p className="mt-2 text-[13px] text-[var(--ink-2)]">
            Linking writes a custody event on the asset — it records the link, it does not alter the
            evidence.
          </p>
        </Section>
      ) : null}

      <Section title={t('cases.linked.title')}>
        {d.assets.length === 0 ? (
          <Empty>
            {t('cases.linked.empty')}
            {canWrite ? ' Link committed evidence above.' : ''}
          </Empty>
        ) : (
          <Table
            head={[
              t('cases.linked.head.captured'),
              t('cases.linked.head.state'),
              t('cases.linked.head.type'),
              t('cases.linked.head.hash'),
              t('cases.linked.head.linked'),
              '',
            ]}
          >
            {d.assets.map((a) => (
              <Row key={a.asset_id}>
                <Cell>{formatWhen(a.captured_at)}</Cell>
                <Cell>
                  <Status tone={ASSET_TONE[a.state] ?? 'neutral'}>
                    {sentence(a.state.replace('_', ' '))}
                  </Status>
                </Cell>
                <Cell>{a.media_type}</Cell>
                <Cell wide>
                  <Mono>{a.merkle_root.slice(0, 16)}…</Mono>
                </Cell>
                <Cell>{formatWhen(a.linked_at)}</Cell>
                <Cell>
                  {canWrite ? (
                    <Button
                      variant="quiet"
                      label={`Unlink ${shortId(a.merkle_root)} from this case`}
                      disabled={unlink.isPending}
                      onClick={() => unlink.mutate(a.asset_id)}
                    >
                      {t('cases.unlink')}
                    </Button>
                  ) : null}
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}
